/** Link a task to a person in the CRM: type a name or email, pick one. */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/ui";

interface ContactHit { id: string; full_name: string | null; email: string | null }

export function ContactPicker({
  id, name, onChange,
}: { id: string | null; name: string | null; onChange: (id: string | null, name: string | null) => void }) {
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ContactHit[]>([]);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get<{ contacts: ContactHit[] }>("/contacts", { q: term, limit: 8 })
        .then((d) => { setHits(d.contacts ?? []); setOpen(true); })
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (id) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 px-2 py-1.5">
        <Avatar name={name} size="xs" />
        <Link to={`/contacts/${id}`} state={{ from: pathname }} className="min-w-0 flex-1 truncate text-sm text-zinc-100 hover:text-indigo-200 hover:underline">
          {name || "Open their record"}
        </Link>
        <button type="button" onClick={() => onChange(null, null)} className="rounded p-1 text-zinc-500 hover:text-zinc-200" aria-label="Unlink person">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={box} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => hits.length && setOpen(true)}
          placeholder="Search people by name or email"
          className="h-9 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
        />
      </div>
      {open && q.trim() && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40">
          {hits.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-zinc-500">Nobody matches "{q.trim()}"</div>
          ) : hits.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => { onChange(h.id, h.full_name || h.email); setQ(""); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-900"
            >
              <Avatar name={h.full_name} email={h.email} size="xs" />
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">{h.full_name || h.email}</span>
              {h.full_name && h.email && <span className="hidden truncate text-[11px] text-zinc-500 sm:block">{h.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
